import React, { useCallback, useState, useEffect, useMemo } from 'react';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';
import { api, fetchProtectedFile } from '../../services/api';
import { Search, Calendar, Printer, FileText, Download, BarChart3, Filter } from 'lucide-react';
import { printResponsibleSummary } from '../../Components/print/printResponsibleSummary';
import PreopPendingAuthorizations from './PreopPendingAuthorizations';
import recordOperationsDownload from '../../utils/recordOperationsDownload';
import PreopDocumentLinks from './PreopDocumentLinks';

function todayYmd() {
  return new Date().toISOString().split('T')[0];
}

function monthStartYmd() {
  const date = new Date();
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}-01`;
}

function formatDateLabel(value) {
  const raw = String(value || '').trim();
  if (!raw) return '-';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  return `${raw.slice(8, 10)}/${raw.slice(5, 7)}/${raw.slice(0, 4)}`;
}

function safeText(value) {
  return String(value ?? '').trim();
}

function normalizeFailureGroup(value) {
  const raw = safeText(value);
  const normalized = raw.toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[_\s-]+/g, '');
  if (!normalized) return '';
  if (normalized === 'datosjson' || normalized === 'datajson' || normalized === 'json') return '';
  return raw;
}

function buildFailureLabel(entry) {
  const group = normalizeFailureGroup(entry?.group);
  const subitem = safeText(entry?.subitem);
  if (subitem) return group ? `${group} / ${subitem}` : subitem;
  return group || 'General';
}

function failureSummary(item) {
  const failures = Array.isArray(item?.failures) ? item.failures : [];
  if (failures.length === 0) return 'Sin fallas detectadas';
  const labels = failures.map((entry) => buildFailureLabel(entry));
  if (labels.length <= 3) return labels.join(', ');
  return `${labels.slice(0, 3).join(', ')} +${labels.length - 3}`;
}

function sanitizeFilePart(value) {
  return String(value || '')
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function buildPreoperationalsWorkbook(rows) {
  const recordsRows = rows.map((row) => ({
    Fecha: row?.date || '',
    Placa: row?.plate || '',
    Marca: row?.brand || '',
    Modelo: row?.model || '',
    Conductor: row?.driver_name || '',
    Fuente: row?.source === 'legacy' ? 'PDF/App' : 'Formulario',
    Kilometraje: row?.mileage ?? '',
    Estado: row?.status || 'Realizado',
    PDF: row?.pdf_url ? 'Si' : 'No',
    Cantidad_fallas: Number(row?.failure_count || 0) || 0,
    Fallas: failureSummary(row),
    Observaciones: row?.notes || '',
  }));

  const failureRows = rows.flatMap((row) => {
    const failures = Array.isArray(row?.failures) ? row.failures : [];
    if (failures.length === 0) {
      return [{
        Fecha: row?.date || '',
        Placa: row?.plate || '',
        Grupo: '',
        Subitem: '',
        Valor_reportado: '',
      }];
    }
    return failures.map((failure) => ({
      Fecha: row?.date || '',
      Placa: row?.plate || '',
      Grupo: failure?.group || '',
      Subitem: failure?.subitem || '',
      Valor_reportado: failure?.value || '',
    }));
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(recordsRows), 'Preoperacionales');
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(failureRows), 'Fallas');
  return workbook;
}

export default function PreoperacionalesModule({ mode = 'preoperacionales' }) {
  const isChecklistMode = mode === 'checklist_por_vehiculo';
  const [selectedDate, setSelectedDate] = useState(todayYmd());
  const [rangeStart, setRangeStart] = useState(monthStartYmd());
  const [rangeEnd, setRangeEnd] = useState(todayYmd());
  const [appliedRange, setAppliedRange] = useState({ from: monthStartYmd(), to: todayYmd() });
  const [loading, setLoading] = useState(false);
  const [dailyData, setDailyData] = useState([]);
  const [rangeData, setRangeData] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all'); // all, done, missing
  const [rangeFilter, setRangeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  // Ciudad de la asignación activa del vehículo (Popayán/Cali/Santander de
  // Quilichao) — Preoperacionales nunca tuvo este filtro; sin él, un
  // coordinador que solo debe ver/gestionar una sede no tenía forma de
  // aislarla entre todos los vehículos de la empresa mezclados en un
  // mismo listado.
  const [cityFilter, setCityFilter] = useState('');

  const fetchDailyData = useCallback(async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      const res = await api.preoperationals.get({ date: selectedDate, city: cityFilter });
      if (res.ok) {
        setDailyData(res.data || []);
      }
    } catch (error) {
      console.error("Error fetching preoperationals:", error);
      setErrorMessage('No fue posible cargar la información diaria de preoperacionales.');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, cityFilter]);

  const fetchRangeData = useCallback(async (from, to) => {
    setLoading(true);
    setErrorMessage('');
    try {
      const res = await api.preoperationals.range({ from, to, limit: 5000, city: cityFilter });
      if (res?.ok) {
        setRangeData(Array.isArray(res.data) ? res.data : []);
      } else {
        setRangeData([]);
        setErrorMessage(res?.error || 'No fue posible consultar el rango seleccionado.');
      }
    } catch (error) {
      console.error('Error fetching range preoperationals:', error);
      setRangeData([]);
      setErrorMessage(error?.message || 'No fue posible consultar el rango seleccionado.');
    } finally {
      setLoading(false);
    }
  }, [cityFilter]);

  useEffect(() => {
    if (isChecklistMode) return;
    fetchRangeData(appliedRange.from, appliedRange.to);
  }, [appliedRange, fetchRangeData, isChecklistMode]);

  useEffect(() => {
    if (!isChecklistMode) return;
    fetchDailyData();
  }, [fetchDailyData, isChecklistMode]);

  useEffect(() => {
    if (!isChecklistMode) return;
    const today = todayYmd();
    if (selectedDate !== today) setSelectedDate(today);
  }, [isChecklistMode, selectedDate]);

  const handleConsultRange = () => {
    if (!rangeStart || !rangeEnd) {
      setErrorMessage('Debes seleccionar fecha inicio y fecha fin.');
      return;
    }
    if (rangeEnd < rangeStart) {
      setErrorMessage('La fecha fin no puede ser menor que la fecha inicio.');
      return;
    }
    setAppliedRange({ from: rangeStart, to: rangeEnd });
  };

  const filteredChecklistData = useMemo(() => {
    return dailyData.filter(item => {
      // Filter by status
      if (filterStatus === 'done' && item.status !== 'Realizado') return false;
      if (filterStatus === 'missing' && item.status !== 'No realizado') return false;
      
      // Filter by search term (plate, brand, model)
      if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        const v = item.vehicle;
        const match = (v.plate?.toLowerCase().includes(lower)) || 
                      (v.brand?.toLowerCase().includes(lower)) || 
                      (v.model?.toLowerCase().includes(lower));
        if (!match) return false;
      }
      return true;
    });
  }, [dailyData, filterStatus, searchTerm]);

  const checklistStats = useMemo(() => {
    const total = dailyData.length;
    const done = dailyData.filter(i => i.status === 'Realizado').length;
    const missing = total - done;
    return { total, done, missing };
  }, [dailyData]);

  const filteredRangeData = useMemo(() => {
    return rangeData.filter((item) => {
      if (rangeFilter === 'with_pdf' && !item?.pdf_url) return false;
      if (rangeFilter === 'with_failures' && Number(item?.failure_count || 0) <= 0) return false;
      if (rangeFilter === 'without_failures' && Number(item?.failure_count || 0) > 0) return false;
      if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        const match = [
          item?.plate,
          item?.brand,
          item?.model,
          item?.driver_name,
          failureSummary(item),
        ].some((value) => String(value || '').toLowerCase().includes(lower));
        if (!match) return false;
      }
      return true;
    });
  }, [rangeData, rangeFilter, searchTerm]);

  const rangeStats = useMemo(() => {
    const total = filteredRangeData.length;
    const withPdf = filteredRangeData.filter((row) => !!row?.pdf_url).length;
    const failures = filteredRangeData.reduce((acc, row) => acc + (Number(row?.failure_count || 0) || 0), 0);
    const uniquePlates = new Set(filteredRangeData.map((row) => safeText(row?.plate)).filter(Boolean)).size;
    return { total, withPdf, failures, uniquePlates };
  }, [filteredRangeData]);

  const plateIndicators = useMemo(() => {
    const map = new Map();
    filteredRangeData.forEach((row) => {
      const plate = safeText(row?.plate) || 'Sin placa';
      const current = map.get(plate) || {
        plate,
        records: 0,
        failures: 0,
        withPdf: 0,
      };
      current.records += 1;
      current.failures += Number(row?.failure_count || 0) || 0;
      if (row?.pdf_url) current.withPdf += 1;
      map.set(plate, current);
    });
    return Array.from(map.values()).sort((a, b) => {
      if (b.failures !== a.failures) return b.failures - a.failures;
      return b.records - a.records;
    }).slice(0, 15);
  }, [filteredRangeData]);

  const subitemIndicators = useMemo(() => {
    const map = new Map();
    filteredRangeData.forEach((row) => {
      const failures = Array.isArray(row?.failures) ? row.failures : [];
      failures.forEach((failure) => {
        const group = normalizeFailureGroup(failure?.group);
        const subitem = safeText(failure?.subitem) || 'Observación';
        const key = `${group}|||${subitem}`;
        const current = map.get(key) || {
          group,
          subitem,
          count: 0,
          plates: new Set(),
        };
        current.count += 1;
        if (row?.plate) current.plates.add(safeText(row.plate));
        map.set(key, current);
      });
    });
    return Array.from(map.values()).map((item) => ({
      group: item.group,
      subitem: item.subitem,
      count: item.count,
      plates: item.plates.size,
    })).sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.group.localeCompare(b.group);
    }).slice(0, 20);
  }, [filteredRangeData]);

  const printDotacion = async (plate) => {
    const safePlate = String(plate || '').trim();
    if (!safePlate) return;
    try {
      const res = await api.vehicleDotacion.get({ plate: safePlate });
      const rows = Array.isArray(res?.data) ? res.data : [];
      if (rows.length === 0) {
        alert('El vehículo no cuenta con asignaciones.');
        return;
      }
      const details = rows.map((r) => ({
        Material: r.material_name || r.material_code || '',
        Serie: r.serial || '',
        Marca: r.brand || '',
        Cantidad: 1,
        Bodega: r.warehouse_name || r.warehouse_id || '',
        'Fecha asignación': r.assigned_at ? String(r.assigned_at).slice(0, 10) : '',
        Observaciones: r.notes || ''
      }));
      const totalsMap = new Map();
      for (const d of details) {
        const key = d.Material || '';
        totalsMap.set(key, (totalsMap.get(key) || 0) + (Number(d.Cantidad) || 0));
      }
      const totals = Array.from(totalsMap.entries()).map(([material, qty]) => ({
        Material: material, 'Total asignado': qty
      }));
      const ts = new Date().toLocaleString('es-CO');
      await printResponsibleSummary({
        responsibleName: `Vehículo ${safePlate}`,
        responsibleDoc: '',
        subjectLabel: 'Vehículo',
        subjectIdLabel: 'Placa',
        warehouseLabel: 'Todas',
        generatedAt: ts,
        assignedBy: '',
        headerTitle: 'AREA TRANSPORTES',
        headerSubtitle: 'ACTA DE ASIGNACION, VALIDACION SE ASIGNACION DE ELEMENTOS DEL VEHICULO',
        qualityKey: 'vehicle_preoperacional',
        headerVariant: 'vehicle',
        footerVariant: 'vehicle',
        details,
        totals
      });
    } catch (e) {
      alert(e?.message || 'No fue posible generar el formato.');
    }
  };

  const handlePrintFromList = async (item) => {
    const plate = item?.vehicle?.plate || '';
    await printDotacion(plate);
  };

  const handleExportExcel = () => {
    if (filteredRangeData.length === 0) {
      alert('No hay registros en el rango seleccionado para exportar.');
      return;
    }
    const workbook = buildPreoperationalsWorkbook(filteredRangeData);
    void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'transportes', reportName: 'Preoperacionales' });
    XLSX.writeFile(workbook, `preoperacionales_${appliedRange.from}_${appliedRange.to}.xlsx`);
  };

  const handleExportPdf = async () => {
    const rowsWithPdf = filteredRangeData.filter((row) => !!row?.pdf_url);
    if (rowsWithPdf.length === 0) {
      alert('No hay PDFs disponibles en el rango seleccionado.');
      return;
    }
    setErrorMessage('');
    const zip = new JSZip();
    const failed = [];

    try {
      const workbook = buildPreoperationalsWorkbook(filteredRangeData);
      const workbookBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      zip.file(`preoperacionales_${appliedRange.from}_${appliedRange.to}.xlsx`, workbookBuffer);
    } catch (error) {
      console.error('Error agregando Excel al ZIP de preoperacionales', error);
      failed.push('Resumen Excel');
    }

    for (let index = 0; index < rowsWithPdf.length; index += 1) {
      const row = rowsWithPdf[index];
      const url = String(row?.pdf_url || '').trim();
      if (!url) continue;
      const plate = sanitizeFilePart(row?.plate || 'sin_placa');
      const date = sanitizeFilePart(row?.date || `registro_${index + 1}`);
      const fileName = `preoperacional_${plate}_${date}.pdf`;
      try {
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        zip.file(fileName, blob);
      } catch (_) {
        failed.push(fileName);
      }
      // Documentos opcionales del conductor (requieren sesión), junto a su PDF.
      for (const doc of (Array.isArray(row?.documents) ? row.documents : [])) {
        if (!doc?.url) continue;
        const ext = /pdf/i.test(doc.mime || '') ? 'pdf' : (/png/i.test(doc.mime || '') ? 'png' : 'jpg');
        const docName = doc.type === 'ORDEN_TRABAJO' ? 'orden_trabajo' : 'aptitud_fisica';
        const docFile = `preoperacional_${plate}_${date}_${docName}.${ext}`;
        try {
          zip.file(docFile, await fetchProtectedFile(doc.url));
        } catch (_) {
          failed.push(docFile);
        }
      }
    }

    const addedFiles = Object.keys(zip.files).length;
    if (addedFiles === 0) {
      setErrorMessage('No fue posible descargar los PDFs del rango seleccionado.');
      return;
    }

    try {
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipUrl = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = zipUrl;
      link.download = `preoperacionales_${appliedRange.from}_${appliedRange.to}.zip`;
      document.body.appendChild(link);
      void recordOperationsDownload({ fileType: 'zip', moduleKey: 'transportes', reportName: 'Preoperacionales (ZIP con PDFs)' });
      link.click();
      link.remove();
      window.URL.revokeObjectURL(zipUrl);
    } catch (error) {
      console.error('Error generando ZIP de preoperacionales', error);
      setErrorMessage('No fue posible generar el archivo ZIP con los PDFs.');
      return;
    }

    if (failed.length > 0) {
      setErrorMessage(`Se descargó el ZIP con ${addedFiles} PDF(s). Algunos archivos no pudieron incluirse: ${failed.slice(0, 5).join(', ')}${failed.length > 5 ? '...' : ''}`);
    }
  };

  return (
    <div className="p-4 space-y-6">
      {!isChecklistMode && <PreopPendingAuthorizations />}
      {isChecklistMode ? (
        <>
          <div className="bg-white p-4 rounded-xl shadow border border-gray-200 flex flex-col md:flex-row gap-4 justify-between items-center">
            <div className="flex items-center gap-4 w-full md:w-auto">
              <div className="text-sm text-gray-600 hidden md:block">
                <span className="font-semibold text-gray-900">{checklistStats.done}</span> realizados de <span className="font-semibold text-gray-900">{checklistStats.total}</span>
              </div>
            </div>

            <div className="flex items-center gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Buscar placa, marca..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500 text-sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <select
                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-500 text-sm"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">Todos</option>
                <option value="done">Realizados</option>
                <option value="missing">No realizados</option>
              </select>

              <select
                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-500 text-sm"
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
              >
                <option value="">Todas las ciudades</option>
                <option value="Popayán">Popayán</option>
                <option value="Cali">Cali</option>
                <option value="Santander de Quilichao">Santander de Quilichao</option>
              </select>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vehículo</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conductor</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ciudad</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Checklist</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-10 text-center text-gray-500">Cargando información...</td>
                    </tr>
                  ) : filteredChecklistData.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-10 text-center text-gray-500">No se encontraron vehículos.</td>
                    </tr>
                  ) : (
                    filteredChecklistData.map((item) => (
                      <tr key={item.vehicle.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-gray-900">{item.vehicle.plate}</span>
                            <span className="text-xs text-gray-500">{item.vehicle.brand} {item.vehicle.model}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.driver_name || item.assigned_driver_name || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {Array.isArray(item.cities) && item.cities.length > 0 ? item.cities.join(' / ') : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => handlePrintFromList(item)}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-semibold text-white shadow-sm hover:shadow-md transition-shadow"
                            style={{ background: 'linear-gradient(90deg, #F07E2D 0%, #F59E0B 100%)' }}
                            title="Imprimir dotación asignada (formato)"
                          >
                            <Printer className="w-4 h-4" /> Imprimir formato
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="bg-white p-4 rounded-xl shadow border border-gray-200 space-y-4">
            <div className="flex flex-col lg:flex-row lg:items-end gap-3">
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4 flex-1">
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-gray-700">Fecha inicio</span>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Calendar className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="date"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500"
                      value={rangeStart}
                      onChange={(e) => setRangeStart(e.target.value)}
                    />
                  </div>
                </label>

                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-gray-700">Fecha fin</span>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Calendar className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="date"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500"
                      value={rangeEnd}
                      onChange={(e) => setRangeEnd(e.target.value)}
                    />
                  </div>
                </label>

                <div className="relative">
                  <span className="mb-1 block text-sm font-medium text-gray-700">Buscar</span>
                  <div className="absolute inset-y-0 left-0 top-6 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Placa, conductor, falla..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500 text-sm"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-gray-700">Filtro</span>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Filter className="h-4 w-4 text-gray-400" />
                    </div>
                    <select
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500 text-sm"
                      value={rangeFilter}
                      onChange={(e) => setRangeFilter(e.target.value)}
                    >
                      <option value="all">Todos los registros</option>
                      <option value="with_pdf">Con PDF</option>
                      <option value="with_failures">Con fallas</option>
                      <option value="without_failures">Sin fallas</option>
                    </select>
                  </div>
                </label>

                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-gray-700">Ciudad</span>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500 text-sm"
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                  >
                    <option value="">Todas las ciudades</option>
                    <option value="Popayán">Popayán</option>
                    <option value="Cali">Cali</option>
                    <option value="Santander de Quilichao">Santander de Quilichao</option>
                  </select>
                </label>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleConsultRange}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-medium hover:bg-orange-700"
                  style={{ backgroundColor: '#F07E2D', borderColor: '#F07E2D', color: '#ffffff' }}
                >
                  <Search className="w-4 h-4" />
                  Consultar
                </button>
                <button
                  type="button"
                  onClick={handleExportPdf}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-300 bg-red-50 text-red-700 text-sm font-medium hover:bg-red-100"
                >
                  <Printer className="w-4 h-4" />
                  Descargar PDFs
                </button>
                <button
                  type="button"
                  onClick={handleExportExcel}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-700 text-sm font-medium hover:bg-emerald-100"
                >
                  <Download className="w-4 h-4" />
                  Exportar Excel
                </button>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-2xl border border-orange-100 bg-orange-50 p-4">
                <div className="text-xs uppercase tracking-wide text-orange-700">Registros</div>
                <div className="mt-1 text-2xl font-semibold text-orange-900">{rangeStats.total}</div>
              </div>
              <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
                <div className="text-xs uppercase tracking-wide text-blue-700">Placas Con Reporte</div>
                <div className="mt-1 text-2xl font-semibold text-blue-900">{rangeStats.uniquePlates}</div>
              </div>
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                <div className="text-xs uppercase tracking-wide text-emerald-700">PDF Disponibles</div>
                <div className="mt-1 text-2xl font-semibold text-emerald-900">{rangeStats.withPdf}</div>
              </div>
              <div className="rounded-2xl border border-yellow-100 bg-yellow-50 p-4">
                <div className="text-xs uppercase tracking-wide text-yellow-700">Fallas Detectadas</div>
                <div className="mt-1 text-2xl font-semibold text-yellow-900">{rangeStats.failures}</div>
              </div>
            </div>

            {errorMessage && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {errorMessage}
              </div>
            )}
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-orange-600" />
                <div className="text-sm font-semibold text-gray-800">Indicadores Por Placa</div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-white">
                    <tr className="text-left text-gray-500">
                      <th className="px-4 py-3">Placa</th>
                      <th className="px-4 py-3">Registros</th>
                      <th className="px-4 py-3">Fallas</th>
                      <th className="px-4 py-3">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plateIndicators.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-gray-500">Sin información para el rango seleccionado.</td>
                      </tr>
                    ) : (
                      plateIndicators.map((item) => (
                        <tr key={item.plate} className="border-t">
                          <td className="px-4 py-3 font-medium text-gray-900">{item.plate}</td>
                          <td className="px-4 py-3 text-gray-700">{item.records}</td>
                          <td className="px-4 py-3 text-gray-700">{item.failures}</td>
                          <td className="px-4 py-3 text-gray-700">{item.withPdf}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-orange-600" />
                <div className="text-sm font-semibold text-gray-800">Indicadores Por Subítem</div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-white">
                    <tr className="text-left text-gray-500">
                      <th className="px-4 py-3">Subítem</th>
                      <th className="px-4 py-3">Ocurrencias</th>
                      <th className="px-4 py-3">Placas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subitemIndicators.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-4 py-8 text-center text-gray-500">Sin fallas detectadas para el rango seleccionado.</td>
                      </tr>
                    ) : (
                      subitemIndicators.map((item) => (
                        <tr key={`${item.group}-${item.subitem}`} className="border-t">
                          <td className="px-4 py-3 text-gray-700">{item.subitem}</td>
                          <td className="px-4 py-3 text-gray-700">{item.count}</td>
                          <td className="px-4 py-3 text-gray-700">{item.plates}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-gray-800">Preoperacionales Diligenciados</div>
                <div className="text-xs text-gray-500">Rango consultado: {formatDateLabel(appliedRange.from)} a {formatDateLabel(appliedRange.to)}</div>
              </div>
              <div className="text-xs text-gray-500">{filteredRangeData.length} registro(s)</div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vehículo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conductor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ciudad</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fuente</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fallas</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PDF</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-10 text-center text-gray-500">Cargando información...</td>
                    </tr>
                  ) : filteredRangeData.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-10 text-center text-gray-500">No se encontraron preoperacionales diligenciados para el rango seleccionado.</td>
                    </tr>
                  ) : (
                    filteredRangeData.map((item, index) => (
                      <tr key={`${item.plate}-${item.date}-${item.source}-${index}`} className="hover:bg-gray-50 align-top">
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{formatDateLabel(item.date)}</td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-gray-900">{item.plate || '-'}</span>
                            <span className="text-xs text-gray-500">{[item.brand, item.model].filter(Boolean).join(' ') || '-'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">{item.driver_name || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {Array.isArray(item.cities) && item.cities.length > 0 ? item.cities.join(' / ') : '-'}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${item.source === 'legacy' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                            {item.source === 'legacy' ? 'PDF/App' : 'Formulario'}
                          </span>
                          {item.approval && item.approval.status !== 'no_aplica' && (
                            <div
                              className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                item.approval.status === 'bloqueado' ? 'bg-red-100 text-red-700'
                                  : item.approval.status === 'aprobado' ? 'bg-emerald-50 text-emerald-700'
                                    : 'bg-amber-50 text-amber-800'}`}
                              title={[item.approval.by, item.approval.at, item.approval.notes].filter(Boolean).join(' · ')}
                            >
                              {item.approval.status === 'bloqueado' ? 'Salida bloqueada' : item.approval.status === 'aprobado' ? 'Aprobado' : 'Pendiente de aprobación'}
                            </div>
                          )}
                          {item.approval?.status === 'bloqueado' && item.approval.notes && (
                            <div className="mt-0.5 text-[11px] text-red-700 max-w-[220px]">{item.approval.notes}</div>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          <div className="font-medium">{Number(item.failure_count || 0) || 0}</div>
                          <div className="text-xs text-gray-500 mt-1">{failureSummary(item)}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm">
                          <button
                            type="button"
                            onClick={() => { if (item.pdf_url) window.open(item.pdf_url, '_blank', 'noopener,noreferrer'); }}
                            disabled={!item.pdf_url}
                            className={`inline-flex items-center gap-1 text-xs font-medium ${item.pdf_url ? 'text-blue-600 hover:text-blue-900' : 'text-gray-300 cursor-not-allowed'}`}
                            title={item.pdf_url ? 'Abrir PDF' : 'PDF no disponible'}
                          >
                            <FileText className="w-4 h-4" /> Ver PDF
                          </button>
                          {/* Adjuntos opcionales que sube el conductor desde la APK */}
                          <PreopDocumentLinks documents={item.documents} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
